import {Children,isValidElement,useEffect,useRef,useState,type HTMLAttributes,type ReactNode} from 'react';
import {useCopy,useUi} from '@platform/ui/config';
import './Notice.css';
import {resolveNotification} from './notificationEngine';

export type NoticeTone='info'|'success'|'warning'|'error';
type Props=HTMLAttributes<HTMLDivElement>&{tone?:NoticeTone;noticeKey?:string;actions?:ReactNode;layout?:'inline'|'screen';dismissKey?:string;onDismiss?:()=>void};

function noticeText(children:ReactNode):string{
 return Children.toArray(children).map(child=>isValidElement<{children?:ReactNode}>(child)?noticeText(child.props.children):String(child)).join('');
}

// Dismiss the message, not its draft/receipt. Stable content survives streaming
// rerenders; a different message or explicit draw context starts visible again.
export function Notice(props:Props){
 const policy=resolveNotification(useUi(),props.noticeKey,props.layout,props.tone);
 if(!policy.enabled)return null;
 return <NoticeContent key={JSON.stringify([props.noticeKey,props.dismissKey,props.noticeKey?undefined:noticeText(props.children)])} {...props} tone={policy.tone} layout={policy.layout} policy={policy}/>;
}

function NoticeContent({tone='info',layout='inline',actions,children,className='',role='status',dismissKey,noticeKey,onDismiss,hidden,policy,...props}:Props&{policy:ReturnType<typeof resolveNotification>}){
 const t=useCopy(),[dismissed,setDismissed]=useState(false);
 const dismissCallback=useRef(onDismiss);dismissCallback.current=onDismiss;
 useEffect(()=>{
  if(!policy.durationMs)return;
  const timer=setTimeout(()=>{setDismissed(true);dismissCallback.current?.();},policy.durationMs);
  return()=>clearTimeout(timer);
 },[policy.durationMs]);
 return <div {...props} hidden={hidden||dismissed} role={role} aria-atomic="true" data-ui="room-notice" data-notice-id={noticeKey} data-dismissible={policy.dismissible} data-tone={tone} data-layout={layout} className={'room-notice '+className}>
  <svg className="room-notice-symbol" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
   {tone==='warning'?<><path d="M12 3 2 21h20Z"/><path d="M12 9v5m0 3h.01"/></>:<><circle cx="12" cy="12" r="9"/>{tone==='success'?<path d="m7 12 3 3 7-7"/>:tone==='error'?<path d="m9 9 6 6m0-6-6 6"/>:<path d="M12 11v6m0-10h.01"/>}</>}
  </svg>
  <div className="room-notice-message">{children}</div>
  {policy.dismissible&&<button type="button" className="room-notice-dismiss" aria-label={t('common.close')} onClick={()=>{setDismissed(true);onDismiss?.();}}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><path d="m6 6 12 12M18 6 6 18"/></svg></button>}
  {actions&&<div className="room-notice-actions">{actions}</div>}
 </div>;
}
